import { ActiveTabKey, QueryKeys } from "../constants";
import { getUserGroups, getUserGroup, getGroupUsers, getUsers } from "../services/apiService";
import { useQuery } from "@tanstack/react-query";
import { ThreeMinutes } from "@Totum/Main/Constants";
import { ICreditDetails, ICreditDetailsPaged } from "@Totum/Main/Domain/ICreditDetails";
import CreditService from "../../../services/CreditService";
import UserService from "../../../services/UserService";
import { IRoleAssignmentDetails } from "@Totum/Main/Domain/IRoleDetails";
import Result from "@Totum/Main/Infrastructure/Result";

/**
 * Hook to fetch all user groups.
 */
export const useUserGroupsQuery = () =>
    useQuery([QueryKeys.GroupsList], getUserGroups, {
        staleTime: ThreeMinutes,
        refetchOnWindowFocus: false,
    });

/**
 * Hook to fetch all user groups for a particular groupId.
 */
export const useUserGroupQuery = (groupId?: string) =>
    useQuery(
        [QueryKeys.UserGroupCurrent, groupId],
        () => {
            if (!groupId) {
                return null;
            }
            return getUserGroup(groupId);
        },
        {
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
        }
    );

/**
 * Hook to fetch all users for a particular groupId.
 */
export const useGroupUsersQuery = (
    groupId?: string,
    page?: number,
    pageSize?: number,
    searchTerm?: string,
    isGroupAdmin?: boolean
) =>
    useQuery(
        [QueryKeys.GroupUsers, groupId, page, pageSize, searchTerm, isGroupAdmin],
        () => {
            if (!groupId) {
                return null;
            }
            return getGroupUsers(groupId, page, pageSize, searchTerm, isGroupAdmin);
        },
        {
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
        }
    );

/**
 * Hook to fetch all users
 */
export const useUsersQuery = (
    page?: number,
    pageSize?: number,
    searchTerm?: string,
    activeTabKey?: string,
    isGroupAdmin?: boolean
) =>
    useQuery(
        [QueryKeys.Users, page, pageSize, searchTerm, activeTabKey, isGroupAdmin],
        () => {
            return getUsers(page, pageSize, searchTerm, isGroupAdmin);
        },
        {
            enabled: activeTabKey === ActiveTabKey.Users,
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
        }
    );

export const useGroupCreditsQuery = (currentGroupId?: string) => {
    return useQuery(
        [QueryKeys.UserGroupCredits, currentGroupId],
        async () => {
            if (currentGroupId === null || currentGroupId === "") return [];
            const service = new CreditService();
            const fetchedCredits = await service.getGroup([currentGroupId]);
            if (fetchedCredits.hasErrors) return [];
            const newCredits: ICreditDetailsPaged[] = fetchedCredits.value.map((x: ICreditDetails) => {
                return { ...x, page: 0 };
            });
            return newCredits;
        },
        {
            initialData: [],
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        }
    );
};

/**
 * Get user roles.
 */
export const useGetUserRoles = (groupUserIds?: string[]) => {
    return useQuery(
        [QueryKeys.UserRoles, groupUserIds],
        async () => {
            if ((groupUserIds ?? []).length === 0) return [];
            const service = new UserService();
            const fetchedRoles: Result<IRoleAssignmentDetails[]> = await service.getRoles(groupUserIds);
            if (!fetchedRoles.hasErrors) {
                return fetchedRoles.value;
            }
            return [];
        },
        {
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        }
    );
};

/**
 * Get user credits.
 */
export const useGetUserCredits = (groupUserIds?: string[]) => {
    return useQuery(
        [QueryKeys.UserCredits, groupUserIds],
        async () => {
            if ((groupUserIds ?? []).length === 0) return [];
            const service = new CreditService();
            const fetchedCredits: Result<ICreditDetails[]> = await service.getUser(groupUserIds);
            if (!fetchedCredits.hasErrors) {
                return fetchedCredits.value;
            }
            return [];
        },
        {
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        }
    );
};
